
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MapPin,
  Filter,
  Calendar,
  User,
  FileText,
  FileDown,
  MessageCircle,
  Edit3,
  X,
  LogOut,
} from "lucide-react";
import ApiHandler from "./apiHandler";
import ClientSidebar from "./ClientSidebar";
import { API_BASE_URL } from "../Api/apiconnector";
import ChatIcon from "../chat/ChatIcon";

const ClientDashboard = () => {
  const [faqs, setFaqs] = React.useState([]);
  const [showFaqs, setShowFaqs] = React.useState(false);

  React.useEffect(() => {
    const fetchFaqs = async () => {
      const response = await ApiHandler.getFaqs("client");
      if (response.success && response.data) {
        setFaqs(response.data.faqs || []);
      }
    };
    fetchFaqs();
  }, []);

  const navigator = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [bodyParts, setBodyParts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);
  const reportsPerPage = 5000;
  const user = JSON.parse(localStorage.getItem("user"))?.user;
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [activeReportDropdown, setActiveReportDropdown] = useState(null); // Add this new state
  const navigate = useNavigate();

  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    modality: "",
    status: "",
    id: "",
    date: "",
  });

  // Fetch reports on mount
  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    fetchBodyParts();
  }, []);

  const fetchBodyParts = async () => {
    const response = await ApiHandler.getBodyParts();
    if (response.success) {
      setBodyParts(response.data);
    } else {
      console.error("Failed to fetch body parts:", response.error);
    }
  };

  //Reset Page When Filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filters.name.trim().length >= 3) {
        handleSearch(filters.name, 'name');
      } else if (filters.name.trim().length === 0) {
        // Clear search when input is empty
        setSearchResults(null);
      }
    }, 500);
  }, [filters.name]);


  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filters.id.trim().length >= 3) {
        handleSearch(filters.id, 'id');
      } else if (filters.id.trim().length === 0) {
        // Clear search when input is empty
        setSearchResults(null);
      }
    }, 500);
  
    return () => clearTimeout(timeoutId);
  }, [filters.id]);
  
  

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await ApiHandler.getTatCounters();

      if (response.success) {
        const transformedData = transformReportsData(
          response.data.results || []
        );
        console.log("Transformed Reports Data:", transformedData);
        setReports(transformedData);
        setNextCursor(response.data.next_cursor);
        setHasMore(!!response.data.next_cursor);
      } else {
        setError(response.error || "Failed to fetch reports");
      }
    } catch (err) {
      setError("Error fetching reports: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  
 const handleSearch = async (searchTerm, searchType) => {
  if (!searchTerm.trim()) {
    // If search is cleared, reset to show all reports
    setSearchResults(null);
    return;
  }

  try {
    setIsSearching(true);
    const response = await ApiHandler.searchPatients(searchTerm);
    
    if (response.success) {
      // Transform the search results to match your reports format
      const transformedResults = transformReportsData(response.data);
      setSearchResults(transformedResults);
    } else {
      console.error("Search failed:", response.error);
      setSearchResults([]);
    }
  } catch (error) {
    console.error("Search error:", error);
    setSearchResults([]);
  } finally {
    setIsSearching(false);
  }
};



  const transformReportsData = (data) => {
    return data.map((item) => ({
      id: item.id,
      patient_id: item.patient_id,
      name: item.patient_name || "N/A",
      email: item.email || "",
      testDate: item.recived_on_db
        ? new Date(item.recived_on_db).toLocaleDateString()
        : "N/A",
      modality: item.modality || "N/A",
      reportDate: item.marked_done_at
        ? new Date(item.marked_done_at).toLocaleDateString()
        : item.study_description || "N/A",
      location: item.body_part_examined || item.study_description || "N/A",
      status: (() => {
        if (item.radiologist?.length === 0 && item.twostepcheck === true) {
          return "Received";
        } else if (item.radiologist?.length > 0 && item.is_done === false && item.twostepcheck === true) {
          return "Reporting";
        } else if (item.radiologist?.length > 0 && item.is_done === true && item.twostepcheck === true) {
          return "Review";
        } else if (item.twostepcheck === false) {
          return "Reported";
        }
        return "Unreported"; // fallback
      })(),
      is_done: item.is_done || false,
      patient_reports: item.patient_reports || [],
      history_files: item.history_files || [],
      age: item.age,
      gender: item.gender,
      testDate: item.study_date,
      studyDescription: item.study_description,
      referringDoctor: item.referring_doctor_name,
      whatsappNumber: item.whatsapp_number,
      notes: item.notes,
      bodyPart: item.body_part_examined,
      isVIP: item.vip || false,
      isUrgent: item.urgent || false,
      isMLC: item.Mlc || false,
      studyId: item.study_id,
      contrastUsed: item.contrast_used,
      isFollowUp: item.is_follow_up,
      imagingViews: item.imaging_views,
      inhousePatient: item.inhouse_patient,
    }));
  };

  const loadMoreReports = async () => {
    if (!nextCursor || isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const response = await ApiHandler.getTatCounters(nextCursor);

      if (response.success) {
        const newData = transformReportsData(response.data.results || []);
        setReports((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const uniqueNewReports = newData.filter(
            (p) => !existingIds.has(p.id)
          );
          return [...prev, ...uniqueNewReports];
        });
        setNextCursor(response.data.next_cursor);
        setHasMore(!!response.data.next_cursor);
      }
    } catch (err) {
      console.error("Error loading more reports:", err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Calculate dynamic summary from reports data
  const summary = React.useMemo(() => {
    const ct = reports.filter((report) => report.modality === "CT").length;
    const mri = reports.filter((report) => report.modality === "MR").length;
    const xray = reports.filter(
      (report) => report.modality === "DX" || report.modality === "CR"
    ).length;
    const totalSent = reports.length;
    const totalReported = reports.filter(
      (report) => report.status === "Reported"
    ).length;
    const totalPending = reports.filter(
      (report) => report.status !== "Reported"
    ).length;

    // You can adjust these calculations based on your business logic
    const totalWallet = 5000; // This might come from a separate API call
    const moneyLeft = totalWallet - totalReported * 10; // Example: $10 per report

    return {
      ct,
      mri,
      xray,
      totalSent,
      totalReported,
      totalPending,
      totalWallet,
      moneyLeft: Math.max(0, moneyLeft), // Ensure it doesn't go negative
    };
  }, [reports]);

// const filteredReports = reports.filter((report) => {
//   // Name filter
//   const nameMatch = (report.name || "")
//     .toLowerCase()
//     .includes((filters.name || "").toLowerCase());
  
//   // Email filter
//   const emailMatch = (report.email || "")
//     .toLowerCase()
//     .includes((filters.email || "").toLowerCase());
  
//   // Patient ID filter
//   const idMatch = (report.patient_id || "")
//     .toLowerCase()
//     .includes((filters.id || "").toLowerCase());
  
//   // Modality filter
//   const modalityMatch = filters.modality === "" || report.modality === filters.modality;
  
//   // Status filter
//   const statusMatch = filters.status === "" || report.status === filters.status;
  
//   // Date filter - FIX HERE
//   let dateMatch = true;
//   if (filters.date) {
//     // Get the filter date in YYYY-MM-DD format
//     const filterDateStr = filters.date; // e.g., "2025-12-06"
    
//     // Parse it properly
//     const [year, month, day] = filterDateStr.split('-');
    
//     // Format to match your API format: DD-MM-YYYY
//     const formattedFilterDate = `${day}-${month}-${year}`;
    
//     // Now compare with report.testDate which is in DD-MM-YYYY format
//     dateMatch = report.testDate === formattedFilterDate;
    
//     // Debug - you can remove this after testing
//     if (filters.date && report.testDate) {
//       console.log('Comparing:', formattedFilterDate, '===', report.testDate, '→', dateMatch);
//     }
//   }
  
//   return nameMatch && emailMatch && idMatch && modalityMatch && statusMatch && dateMatch;
// });


// Use search results if available, otherwise use filtered reports


const displayReports = searchResults !== null ? searchResults : reports;

const filteredReports = displayReports.filter((report) => {
  // Keep your existing filter logic but remove name and id filters since search handles those
  const emailMatch = (report.email || "")
    .toLowerCase()
    .includes((filters.email || "").toLowerCase());
  
  const modalityMatch = filters.modality === "" || report.modality === filters.modality;
  const statusMatch = filters.status === "" || report.status === filters.status;
  
  let dateMatch = true;
  if (filters.date) {
    const [year, month, day] = filters.date.split('-');
    const formattedFilterDate = `${day}-${month}-${year}`;
    dateMatch = report.testDate === formattedFilterDate;
  }
  
  return emailMatch && modalityMatch && statusMatch && dateMatch;
});



  // const handleSaveReport = async (reportData, historyFiles = null) => {
  //   try {
  //     const originalReport = reports.find((r) => r.id === selectedReport.id);
  //     if (!originalReport) return;

  //     // Map frontend field names to backend field names
  //     const apiData = {
  //       patient_name: reportData.patient_name,
  //       patient_id: reportData.patient_id,
  //       age: reportData.age,
  //       gender: reportData.gender,
  //       study_description: reportData.study_description,
  //       notes: reportData.notes,
  //       body_part_examined: reportData.body_part_examined,
  //       referring_doctor_name: reportData.referring_doctor_name,
  //       email: reportData.email,
  //       whatsapp_number: reportData.whatsapp_number,
  //       contrast_used:
  //         reportData.contrast_used === "true" ||
  //         reportData.contrast_used === true,
  //       is_follow_up:
  //         reportData.is_follow_up === "true" ||
  //         reportData.is_follow_up === true,
  //       imaging_views: reportData.imaging_views,
  //     };

  //     const response = await ApiHandler.updateDicomReport(
  //       selectedReport.id,
  //       apiData
  //     );

  //     if (response.success) {
  //       // If there's a history file, upload it separately
  //       if (historyFiles && historyFiles.length > 0) {
  //         setUploadingFile(true);
  //         const uploadResponse = await ApiHandler.uploadHistoryFile(
  //           selectedReport.id,
  //           historyFiles
  //         );
  //         setUploadingFile(false);

  //         if (!uploadResponse.success) {
  //           alert(
  //             "Report updated but history file upload failed: " +
  //               uploadResponse.error
  //           );
  //         } else {
  //           alert("Report updated and history file uploaded successfully!");
  //         }
  //       }

  //       await fetchReports();
  //       setSelectedReport(null);
  //     } else {
  //       alert("Failed to update report: " + response.error);
  //     }
  //   } catch (err) {
  //     setUploadingFile(false);
  //     alert("Error updating report: " + err.message);
  //   }
  // };      



//   const handleSaveReport = async (reportData, historyFiles = null) => {
//   try {
//     const originalReport = reports.find((r) => r.id === selectedReport.id);
//     if (!originalReport) return;

//     // Map frontend field names to backend field names
//     const apiData = {
//       patient_name: reportData.patient_name,
//       patient_id: reportData.patient_id,
//       age: reportData.age,
//       gender: reportData.gender,
//       study_description: reportData.study_description,
//       notes: reportData.notes,
//       body_part_examined: reportData.body_part_examined,
//       referring_doctor_name: reportData.referring_doctor_name,
//       email: reportData.email,
//       whatsapp_number: reportData.whatsapp_number,
//       contrast_used:
//         reportData.contrast_used === "true" ||
//         reportData.contrast_used === true,
//       is_follow_up:
//         reportData.is_follow_up === "true" ||
//         reportData.is_follow_up === true,
//       imaging_views: reportData.imaging_views,
//     };

//     const response = await ApiHandler.updateDicomReport(
//       selectedReport.id,
//       apiData
//     );

//     if (response.success) {
//       // If there's a history file, upload it separately
//       if (historyFiles && historyFiles.length > 0) {
//         setUploadingFile(true);
//         try {
//           const uploadResponse = await ApiHandler.uploadHistoryFile(
//             selectedReport.id,
//             historyFiles
//           );

//           if (!uploadResponse.success) {
//             alert(
//               "Report updated but history file upload failed: " +
//                 uploadResponse.error
//             );
//           } else {
//             alert("Report updated and history file uploaded successfully!");
//           }
//         } catch (uploadErr) {
//           alert(
//             "Report updated but history file upload failed: " +
//               uploadErr.message
//           );
//         } finally {
//           // Always reset — even if the upload call hangs or throws —
//           // so Save Changes never stays stuck disabled for other reports.
//           setUploadingFile(false);
//         }
//       }

//       await fetchReports();
//       setSelectedReport(null);
//     } else {
//       alert("Failed to update report: " + response.error);
//     }
//   } catch (err) {
//     setUploadingFile(false);
//     alert("Error updating report: " + err.message);
//   }
// };


const handleSaveReport = async (reportData, historyFiles = null) => {
  try {
    // Map frontend field names to backend field names
    const apiData = {
      patient_name: reportData.patient_name,
      patient_id: reportData.patient_id,
      age: reportData.age,
      gender: reportData.gender,
      study_description: reportData.study_description,
      notes: reportData.notes,
      body_part_examined: reportData.body_part_examined,
      referring_doctor_name: reportData.referring_doctor_name,
      email: reportData.email,
      whatsapp_number: reportData.whatsapp_number,
      contrast_used:
        reportData.contrast_used === "true" ||
        reportData.contrast_used === true,
      is_follow_up:
        reportData.is_follow_up === "true" ||
        reportData.is_follow_up === true,
      imaging_views: reportData.imaging_views,
    };

    const response = await ApiHandler.updateDicomReport(
      selectedReport.id,
      apiData
    );

    if (response.success) {
      // If there's a history file, upload it separately
      if (historyFiles && historyFiles.length > 0) {
        setUploadingFile(true);
        try {
          const uploadResponse = await ApiHandler.uploadHistoryFile(
            selectedReport.id,
            historyFiles
          );

          if (!uploadResponse.success) {
            alert(
              "Report updated but history file upload failed: " +
                uploadResponse.error
            );
          } else {
            alert("Report updated and history file uploaded successfully!");
          }
        } catch (uploadErr) {
          alert(
            "Report updated but history file upload failed: " +
              uploadErr.message
          );
        } finally {
          setUploadingFile(false);
        }
      }

      // Build the updated fields (matches the shape produced by transformReportsData)
      const updatedFields = {
        name: reportData.patient_name,
        patient_id: reportData.patient_id,
        age: reportData.age,
        gender: reportData.gender,
        studyDescription: reportData.study_description,
        notes: reportData.notes,
        bodyPart: reportData.body_part_examined,
        location: reportData.body_part_examined,
        referringDoctor: reportData.referring_doctor_name,
        email: reportData.email,
        whatsappNumber: reportData.whatsapp_number,
        contrastUsed: apiData.contrast_used,
        isFollowUp: apiData.is_follow_up,
        imagingViews: reportData.imaging_views,
      };

      // Patch BOTH lists so whichever is currently rendered shows fresh data immediately
      setReports((prev) =>
        prev.map((r) =>
          r.id === selectedReport.id ? { ...r, ...updatedFields } : r
        )
      );

      setSearchResults((prev) =>
        prev !== null
          ? prev.map((r) =>
              r.id === selectedReport.id ? { ...r, ...updatedFields } : r
            )
          : prev
      );

      setSelectedReport(null); // close modal immediately
      fetchReports(); // background sync of page-1 cache (not awaited, doesn't block UI)
    } else {
      alert("Failed to update report: " + response.error);
    }
  } catch (err) {
    setUploadingFile(false);
    alert("Error updating report: " + err.message);
  }
};

  const handleStatusToggle = async (reportId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Ready" ? "Pending" : "Ready";
      const isDone = newStatus === "Ready";
      const response = await ApiHandler.updateDicomReport(reportId, { isDone });
      if (response.success) {
        await fetchReports();
      } else {
        alert("Failed to update status: " + response.error);
      }
    } catch (err) {
      alert("Error updating status: " + err.message);
    }
  };

  //Pagination Logic
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * reportsPerPage,
    currentPage * reportsPerPage
  );

  const totalPages = Math.ceil(filteredReports.length / reportsPerPage);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    else if (hour < 18) return "Good Afternoon";
    else return "Good Evening";
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreReports();
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoadingMore]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button
            onClick={fetchReports}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-screen overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Sidebar */}
        {showSidebar && <ClientSidebar summary={summary} />}

        {/* Main Content */}
        <div className="flex-1 flex flex-col p-6 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">
                <span className={"text-white"}>
                  <img
                    src="https://u4rad.com/static/media/Logo.c9920d154c922ea9e355.png"
                    alt="U4rad"
                    style={{
                      height: 50,
                      backgroundColor: "transparent",
                      borderRadius: 6, // optional
                      padding: 2, // optional (to give space around logo)
                    }}
                  />
                </span>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <h2
                style={{
                  fontSize: 18,
                  margin: 0,
                  fontWeight: 600,
                  color: "#0B0B0B",
                  display: window.innerWidth <= 600 ? "none" : "block",
                }}
              >
                {getGreeting()}, {firstName} {lastName}
              </h2>
              {/* FAQ Section */}

              {/* Logout Button */}
              <button
                onClick={() => {
                  // Clear stored tokens or user data
                  localStorage.removeItem("token");
                  sessionStorage.clear();

                  // Redirect to login
                  window.location.href = "/";
                }}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 transition"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline font-medium">Logout</span>
              </button>

              {/* Sidebar toggle button */}
              <button
                className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center hover:scale-105 transform transition"
                onClick={() => setShowSidebar(!showSidebar)}
              >
                <User className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          {/* Filters - Single Button */}
<div className="flex flex-wrap gap-4 mb-4">
  <input
    type="text"
    placeholder="Search by Name"
    value={filters.name}
    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
    className="border p-2 rounded flex-1"
  />
  <input
    type="text"
    placeholder="Search by Patient ID"
    value={filters.id}
    onChange={(e) => setFilters({ ...filters, id: e.target.value })}
    className="border p-2 rounded flex-1"
  />

 <button
  onClick={() => navigator("/report-Page")}
  className="flex items-center gap-2 px-5 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 shadow-md font-medium"
>
  <FileText className="w-4 h-4" />
  <span>View Reports</span>
</button>

  {/* Open Filters Button */}
  <button
    onClick={() => setShowFiltersModal(true)}
    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md"
  >
    <Filter className="w-5 h-5" />
    <span className="font-semibold">Open Filters</span>
    {(filters.modality || filters.status || filters.date) && (
      
      <span className="bg-white text-blue-600 text-xs px-2 py-0.5 rounded-full font-bold">
        Active-{filteredReports.length}
      </span>
    )}
  </button>

  {/* FAQ Button */}
  <div>
    <button
      onClick={() => setShowFaqs(!showFaqs)}
      className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200 shadow-md"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span className="font-semibold">FAQ</span>
      <span className="bg-white text-blue-600 text-xs px-2 py-0.5 rounded-full font-bold">
        {faqs.length}
      </span>
    </button>
  </div>
</div>
            {showFaqs && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={() => setShowFaqs(false)}
              >
                <div
                  className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h2 className="text-white font-bold text-xl">
                        Help & Support
                      </h2>
                    </div>
                    <button
                      onClick={() => setShowFaqs(false)}
                      className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  <div
                    className="flex-1 overflow-y-auto p-6"
                    style={{ scrollbarWidth: "thin" }}
                  >
                    {faqs.length > 0 ? (
                      <div className="space-y-4">
                        {faqs.map((faq, index) => (
                          <div
                            key={faq.id}
                            className="bg-gradient-to-r from-blue-50 to-white border-l-4 border-blue-500 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex gap-4">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                                  Q{index + 1}
                                </div>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-gray-900 mb-2 text-lg">
                                  {faq.question}
                                </h3>
                                <p className="text-gray-700 leading-relaxed">
                                  {faq.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <svg
                          className="w-20 h-20 mx-auto text-gray-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-gray-500 font-semibold text-lg">
                          No FAQs available
                        </p>
                        <p className="text-gray-400 text-sm mt-2">
                          Check back later for helpful information
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="border-t px-6 py-4 bg-gray-50 rounded-b-xl">
                    <button
                      onClick={() => setShowFaqs(false)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            

          {/* Count */}
          {/* <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" className="h-4 w-4" /> Select All
          </label>
          <span className="text-sm font-semibold text-gray-600">
            👤 Total Reports: {filteredReports.length}
          </span>
        </div> */}

          {/* Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden flex-1 min-h-0">
            <div className="overflow-y-auto h-full">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gradient-to-r from-gray-800 to-gray-700 text-white sticky top-0 z-10">
                  <tr>
                    <th className="p-3 text-left">Patient ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Age</th>
                    <th className="p-3 text-left">Gender</th>
                    <th className="p-3 text-left">Study Date</th>
                    <th className="p-3 text-left">Study Description</th>
                    <th className="p-3 text-left">Notes</th>
                    <th className="p-3 text-left">Body Part</th>
                    <th className="p-3 text-left">Modality</th>
                    <th className="p-3 text-left">Doctor</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isSearching ? (
                     <tr>
                       <td colSpan="12" className="text-center py-8">
                         <div className="flex flex-col items-center gap-3">
                           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                           <p className="text-gray-600">Searching patients...</p>
                         </div>
                       </td>
                     </tr>
                   ) : paginatedReports.length === 0 ? (
                     <tr>
                       <td colSpan="12" className="text-center py-8">
                         <p className="text-gray-600">No patients found</p>
                       </td>
                     </tr>
                   ) : (
                  paginatedReports.map((report, index) => (
                    <tr
                      key={report.id}
                      className={`transition hover:bg-gray-50 ${
                        index % 2 === 0 ? "bg-gray-50/30" : "bg-white"
                      }`}
                    >
                      <td className="p-2 font-medium text-gray-700">
                        {report.patient_id}
                      </td>
                      <td className="p-2">{report.name}</td>
                      {/* <td className="p-2 text-gray-600">{report.email}</td> */}
                      {/* Age */}
                      <td className="p-2">{report.age || "N/A"}</td>
                      {/* Gender */}
                      <td className="p-2">{report.gender || "N/A"}</td>
                      <td className="p-2">{report.testDate}</td>
                      {/* <td className="p-2">{report.reportDate}</td>
                      <td className="p-2">{report.modality}</td>
                      <td className="p-2">{report.studyDescription}</td> */}
                      <td className="p-2">{report.studyDescription}</td>
                      <td className="p-2">{report.notes || "N/A"}</td>
                      <td className="p-2">
                        {report.body_part_examined || report.bodyPart || report.location || "N/A"}
                      </td>
                      <td className="p-2">{report.modality}</td>
                      <td className="p-2">
                        {report.referringDoctor || "N/A"}
                      </td>
                      <td className="p-3">
                       <span
                        className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer ${
                          report.status === "Reported"
                            ? "bg-green-100 text-green-700"
                            : report.status === "Review"
                            ? "bg-blue-100 text-blue-700"
                            : report.status === "Reporting"
                            ? "bg-orange-100 text-orange-700"
                            : report.status === "Received"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                        onClick={() =>
                          handleStatusToggle(report.id, report.status)
                        }
                      >
                        {report.status}
                      </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/clientviwer?id=${report.id}`);
                            }}
                            className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 hover:from-blue-200 hover:to-blue-300 border border-blue-300 transition-all duration-200 transform hover:scale-105 dark:from-blue-900/30 dark:to-blue-800/30 dark:text-blue-200 dark:hover:from-blue-800 dark:hover:to-blue-700 dark:border-blue-600"
                          >
                            👁️ View
                          </button>

                          {/* Patient Reports from API */}
                          {/* Patient Reports from API - with dropdown */}
                          {report.patient_reports &&
                            report.patient_reports.length > 0 &&
                            report.patient_reports.map((reportFile, i) => (
                              <div
                                key={`report-${i}`}
                                className="relative inline-block text-left"
                              >
                                <button
                                  onClick={() =>
                                    setActiveReportDropdown(
                                      activeReportDropdown ===
                                        `${report.id}-report-${i}`
                                        ? null
                                        : `${report.id}-report-${i}`
                                    )
                                  }
                                  className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 hover:bg-green-200 text-green-700"
                                >
                                  <FileText className="w-4 h-4" />
                                  {report.patient_reports.length === 1
                                    ? "Report"
                                    : `Report ${i + 1}`}
                                </button>

                                {activeReportDropdown ===
                                  `${report.id}-report-${i}` && (
                                  <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-10">
                                    {/* Convert PDF to Word */}
                                    <button
                                      onClick={async () => {
                                        try {
                                          if (!reportFile?.file_key) {
                                            alert("No report file URL found.");
                                            return;
                                          }
                                    
                                          const fileUrl = encodeURIComponent(reportFile.file_key);
                                    
                                          const response = await fetch(`${API_BASE_URL}/convert-pdf-to-word/?pdf_key=${fileUrl}`, {
                                            method: "GET",
                                            credentials: "include",
                                          });
                                    
                                          if (!response.ok) throw new Error("Conversion failed");
                                    
                                          const blob = await response.blob();
                                          const disposition = response.headers.get("content-disposition");
                                          let filename = "Converted_Report.doc";
                                          if (disposition && disposition.includes("filename=")) {
                                            filename = disposition.split("filename=")[1].replace(/"/g, "");
                                          }
                                    
                                          const url = window.URL.createObjectURL(blob);
                                          const a = document.createElement("a");
                                          a.href = url;
                                          a.download = filename;
                                          document.body.appendChild(a);
                                          a.click();
                                          a.remove();
                                          window.URL.revokeObjectURL(url);
                                        } catch (err) {
                                          console.error("Word conversion error:", err);
                                          alert("Error converting PDF to Word");
                                        }
                                      }}
                                      className="flex items-center w-full gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                                    >
                                      📄 Convert to Word
                                    </button>
                                    {/* aman */}
                                     <button
                                       onClick={async () => {
                                         try {
                                           const urlResult = await ApiHandler.getPresignedUrl(reportFile.file_key);
                                           if (urlResult.success && urlResult.url) {
                                             window.open(urlResult.url, '_blank');
                                           } else {
                                             alert('Failed to open report');
                                           }
                                           setActiveReportDropdown(null);
                                         } catch (error) {
                                           console.error('Error opening report:', error);
                                           alert('Failed to open report');
                                         }
                                       }}
                                       className="flex items-center w-full gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                                     >
                                       PDF
                                     </button>
                                    {/* Download with Logo */}
                                    <button
                                      onClick={async () => {
                                        try {
                                          if (!reportFile?.file_key) {
                                            alert("No report file URL found.");
                                            return;
                                          }
                                    
                                          const fileUrl = encodeURIComponent(reportFile.file_key);
                                    
                                          const response = await fetch(`${API_BASE_URL}/download-pdf-with-logo/?pdf_key=${fileUrl}`, {
                                            method: "GET",
                                            credentials: "include",
                                          });
                                    
                                          if (!response.ok) throw new Error("Failed to generate PDF with logo");
                                    
                                          const blob = await response.blob();
                                          const disposition = response.headers.get("content-disposition");
                                          // let filename = "Report_with_logo.pdf";
                                          // if (disposition && disposition.includes("filename=")) {
                                          //   filename = disposition.split("filename=")[1].replace(/"/g, "");
                                          // }
                                          const patientId = report.patient_id || "ID";
                                          const patientName = (report.name || "Patient").replace(/\s+/g, '_'); // Replaces spaces with underscores
                                          const filename = `${patientId}_${patientName}.pdf`;
                                    
                                          const url = window.URL.createObjectURL(blob);
                                          const a = document.createElement("a");
                                          a.href = url;
                                          a.download = filename;
                                          document.body.appendChild(a);
                                          a.click();
                                          a.remove();
                                          window.URL.revokeObjectURL(url);
                                        } catch (err) {
                                          console.error("Download with logo error:", err);
                                          alert("Error generating PDF with logo");
                                        }
                                      }}
                                      className="flex items-center w-full gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                                    >
                                      🏢 Download with Logo
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}

                          {/* History Files from API */}
                          {report.history_files &&
                            report.history_files.length > 0 &&
                            report.history_files.map((historyFile, i) => {
                              return (
                                <button
                                  key={`history-${i}`}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const urlResult = await ApiHandler.getPresignedUrl(historyFile);
                                      if (urlResult.success && urlResult.url) {
                                        window.open(urlResult.url, '_blank');
                                      } else {
                                        alert('Failed to open history file');
                                      }
                                    } catch (error) {
                                      console.error('Error opening history file:', error);
                                      alert('Failed to open history file');
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-100 to-blue-100 dark:from-indigo-900/30 dark:to-blue-900/30 text-indigo-800 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-600 hover:from-indigo-200 hover:to-blue-200 dark:hover:from-indigo-800 dark:hover:to-blue-800 transition-all duration-200"
                                >
                                  <FileDown className="w-4 h-4" />
                                  {report.history_files.length === 1 ? "History" : `H${i + 1}`}
                                </button>
                              );
                            })}

                          <button
                            disabled={report.is_done}
                            title={
                              report.is_done
                                ? "Completed — editing disabled"
                                : "Edit report"
                            }
                            onClick={() =>
                              !report.is_done && setSelectedReport(report)
                            }
                            className={
                              report.is_done
                                ? "inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 transform bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed opacity-60"
                                : "flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                            }
                          >
                            <Edit3 className="w-4 h-4" /> Edit
                          </button>

                          {/* Checkboxes for VIP, Urgent, MLC */}
                          <div className="flex items-center gap-2">
                            <label className="flex items-center text-xs font-semibold px-2 py-1 rounded bg-purple-100 text-purple-800 hover:bg-purple-200 cursor-pointer">
                              <input
                                type="checkbox"
                                className="mr-1 accent-purple-600"
                                checked={report.isVIP}
                                onChange={async (e) => {
                                  const checked = e.target.checked;
                                  setReports((prev) =>
                                    prev.map((r) =>
                                      r.id === report.id
                                        ? { ...r, isVIP: checked }
                                        : r
                                    )
                                  );
                                  try {
                                    await ApiHandler.updateDicomReport(
                                      report.id,
                                      { vip: checked }
                                    );
                                  } catch (error) {
                                    alert(
                                      "Failed to update VIP status: " +
                                        error.message
                                    );
                                    setReports((prev) =>
                                      prev.map((r) =>
                                        r.id === report.id
                                          ? { ...r, isVIP: !checked }
                                          : r
                                      )
                                    );
                                  }
                                }}
                              />
                              VIP
                            </label>

                            <label className="flex items-center text-xs font-semibold px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer">
                              <input
                                type="checkbox"
                                className="mr-1 accent-red-600"
                                checked={report.isUrgent}
                                onChange={async (e) => {
                                  const checked = e.target.checked;
                                  setReports((prev) =>
                                    prev.map((r) =>
                                      r.id === report.id
                                        ? { ...r, isUrgent: checked }
                                        : r
                                    )
                                  );
                                  try {
                                    await ApiHandler.updateDicomReport(
                                      report.id,
                                      { urgent: checked }
                                    );
                                  } catch (error) {
                                    alert(
                                      "Failed to update Urgent status: " +
                                        error.message
                                    );
                                    setReports((prev) =>
                                      prev.map((r) =>
                                        r.id === report.id
                                          ? { ...r, isUrgent: !checked }
                                          : r
                                      )
                                    );
                                  }
                                }}
                              />
                              Urgent
                            </label>

                            <label className="flex items-center text-xs font-semibold px-2 py-1 rounded bg-yellow-100 text-yellow-900 hover:bg-yellow-200 cursor-pointer">
                              <input
                                type="checkbox"
                                className="mr-1 accent-yellow-500"
                                checked={report.isMLC}
                                onChange={async (e) => {
                                  const checked = e.target.checked;
                                  setReports((prev) =>
                                    prev.map((r) =>
                                      r.id === report.id
                                        ? { ...r, isMLC: checked }
                                        : r
                                    )
                                  );
                                  try {
                                    await ApiHandler.updateDicomReport(
                                      report.id,
                                      { Mlc: checked }
                                    );
                                  } catch (error) {
                                    alert(
                                      "Failed to update MLC status: " +
                                        error.message
                                    );
                                    setReports((prev) =>
                                      prev.map((r) =>
                                        r.id === report.id
                                          ? { ...r, isMLC: !checked }
                                          : r
                                      )
                                    );
                                  }
                                }}
                              />
                              MLC
                            </label>
                          </div>
                        </div>
                      </td>
                    </tr>
                 ) ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-4">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`px-3 py-1 rounded ${
                        currentPage === i + 1
                          ? "bg-red-500 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
              <div
                ref={observerTarget}
                className="w-full py-4 flex justify-center"
              >
                {!searchResults && hasMore && (
                  <button
                    onClick={loadMoreReports}
                    disabled={isLoadingMore}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <span>Show More</span>
                        {/* Optional: Add a down arrow icon */}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                )}
                {!hasMore && reports.length > 0 && (
                  <p className="text-gray-500 text-sm">
                    No more reports to load
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg p-6 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                Edit DICOM Data
              </h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const newData = {};
                fd.forEach((value, key) => {
                  if (key !== "history_file") {
                    newData[key] = value;
                  }
                });

                // Get the history file separately
                const historyFiles = Array.from(
                  fd.getAll("history_file")
                ).filter((f) => f && f.size > 0);

                handleSaveReport(
                  newData,
                  historyFiles.length ? historyFiles : null
                );
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <label className="flex flex-col">
                  <span>Name:</span>
                  <input
                    type="text"
                    name="patient_name"
                    defaultValue={selectedReport.name}
                    className="border rounded p-2"
                  />
                </label>
                <label className="flex flex-col">
                  <span>Patient Id:</span>
                  <input
                    type="text"
                    name="patient_id"
                    defaultValue={selectedReport.patient_id || ""}
                    className="border rounded p-2"
                  />
                </label>
                <label className="flex flex-col">
                  <span>Age:</span>
                  <input
                    type="text"
                    name="age"
                    defaultValue={selectedReport.age || ""}
                    className="border rounded p-2 w-20"
                  />
                </label>
                <label className="flex flex-col">
                  <span>Gender:</span>
                  <select
                    name="gender"
                    defaultValue={selectedReport.gender || ""}
                    className="border rounded p-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </label>

                <label className="flex flex-col">
                  <span>Study Date:</span>
                  <input
                    type="text"
                    name="study_date"
                    defaultValue={selectedReport.testDate}
                    className="border rounded p-2"
                  />
                </label>
                <label className="flex flex-col">
                  <span>Study Description:</span>
                  <input
                    type="text"
                    name="study_description"
                    defaultValue={selectedReport.studyDescription || ""}
                    className="border rounded p-2"
                  />
                </label>
              </div>

              <label className="flex flex-col">
                <span>Notes:</span>
                <textarea
                  name="notes"
                  defaultValue={selectedReport.notes || ""}
                  className="border rounded p-2"
                />
              </label>

<label className="flex flex-col">
  <span>Body Part Examined:</span>
  <select
    name="body_part_examined"
    value={selectedReport?.bodyPart || ""}
    onChange={(e) =>
      setSelectedReport((prev) => ({
        ...prev,
        bodyPart: e.target.value,
      }))
    }
    className="border rounded p-2"
  >
    <option value="" disabled>
      Select Body Part
    </option>
    {/* Show current database value if it doesn't exist in dropdown */}
    {selectedReport?.bodyPart && 
     !bodyParts.some((part) => part.name === selectedReport.bodyPart) && (
      <option value={selectedReport.bodyPart}>
        {selectedReport.bodyPart}
      </option>
    )}
    {bodyParts.map((part) => (
      <option key={part.id} value={part.name}>
        {part.name}
      </option>
    ))}
  </select>
</label>
              <label className="flex flex-col">
                <span>Referring Doctor Name:</span>
                <input
                  type="text"
                  name="referring_doctor_name"
                  defaultValue={selectedReport.referringDoctor || ""}
                  className="border rounded p-2"
                />
              </label>
              <label className="flex flex-col">
                <span>Email:</span>
                <input
                  type="text"
                  name="email"
                  defaultValue={selectedReport.email || ""}
                  className="border rounded p-2"
                />
              </label>
              <label className="flex flex-col">
                <span>WhatsApp Number:</span>
                <input
                  type="text"
                  name="whatsapp_number"
                  defaultValue={selectedReport.whatsappNumber || ""}
                  className="border rounded p-2"
                />
              </label>
              <label className="flex flex-col">
                <span>Patient History File:</span>
                <input
                  type="file"
                  name="history_file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  className="border rounded p-2"
                />
                <span className="text-xs text-gray-500 mt-1">
                  Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB)
                </span>
              </label>

              {/* Checklist Section */}
              <div className="mt-6 border-t pt-4">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Checklist for Radiographer / Coordinator (Pre-Scan
                  Verification)
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p>1. Body Part Confirmation</p>
                    <p className="text-gray-600 ml-2">
                      ➝ Have you filled the body part details?
                    </p>
                  </div>
                  <div>
                    <p>2. Contrast Imaging Verification</p>
                    <label className="mr-4">
                      <input
                        type="radio"
                        name="contrast_used"
                        value="true"
                        className="mr-1"
                        defaultChecked={selectedReport.contrastUsed}
                      />{" "}
                      Yes
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="contrast_used"
                        value="false"
                        className="mr-1"
                        defaultChecked={!selectedReport.contrastUsed}
                      />{" "}
                      No
                    </label>
                  </div>
                  <div>
                    <p>3. Comparative / Follow-Up Verification</p>
                    <label className="mr-4">
                      <input
                        type="radio"
                        name="is_follow_up"
                        value="true"
                        className="mr-1"
                        defaultChecked={selectedReport.isFollowUp}
                      />{" "}
                      Yes
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="is_follow_up"
                        value="false"
                        className="mr-1"
                        defaultChecked={!selectedReport.isFollowUp}
                      />{" "}
                      No
                    </label>
                  </div>
                  <div>
                    <p>4. Imaging View Verification (for X-ray)</p>
                    <div className="flex flex-wrap gap-4 ml-2">
                      <label>
                        <input
                          type="checkbox"
                          name="imaging_views"
                          value="AP"
                          className="mr-1"
                        />{" "}
                        AP
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="imaging_views"
                          value="PA"
                          className="mr-1"
                        />{" "}
                        PA
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="imaging_views"
                          value="Lateral"
                          className="mr-1"
                        />{" "}
                        Lateral
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="imaging_views"
                          value="Oblique"
                          className="mr-1"
                        />{" "}
                        Oblique
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setSelectedReport(null)}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={uploadingFile}
                  className={`px-4 py-2 rounded text-white ${
                    uploadingFile
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {uploadingFile ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </span>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters Modal */}
{showFiltersModal && (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    onClick={() => setShowFiltersModal(false)}
  >
    <div
      className="bg-white rounded-xl shadow-2xl w-full max-w-md"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-3">
          <Filter className="w-6 h-6 text-white" />
          <h2 className="text-white font-bold text-xl">Filter Reports</h2>
        </div>
        <button
          onClick={() => setShowFiltersModal(false)}
          className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Modality Filter */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Modality
          </label>
          <select
            value={filters.modality}
            onChange={(e) =>
              setFilters({ ...filters, modality: e.target.value })
            }
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Modalities</option>
            <option value="CT">CT</option>
            <option value="MR">MR</option>
            <option value="DX">DX</option>
            <option value="CR">CR</option>
            <option value="SR">SR</option>
            <option value="PR">PR</option>
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value })
            }
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="Received">Received</option>
            <option value="Reporting">Reporting</option>
            <option value="Review">Review</option>
            <option value="Reported">Reported</option>
          </select>
        </div>

        {/* Date Filter */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Study Date
          </label>
          <input
            type="date"
            value={filters.date}
            onChange={(e) =>
              setFilters({ ...filters, date: e.target.value })
            }
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="border-t px-6 py-4 bg-gray-50 rounded-b-xl flex gap-3">
        <button
          onClick={() => {
            setFilters({
              name: filters.name,
              email: filters.email,
              id: filters.id,
              modality: "",
              status: "",
              date: "",
            });
            setSearchResults(null); 
          }}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
        >
          Clear Filters
        </button>
        <button
          onClick={() => {
          console.log("Filter Date:", filters.date);
          console.log("Sample Report Date:", reports[0]?.testDate);
          console.log("Formatted:", new Date(filters.date).toLocaleDateString('en-GB'));
          setShowFiltersModal(false);
        }}
          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
        >
          Apply Filters
        </button>
      </div>
    </div>
  </div>
)}
<ChatIcon role="client" />
    </>
  );
};

export default ClientDashboard;
